import { useState } from 'react';
import { BoothCard, BoothInput, BoothButton } from '@photobooth/ui';
import { validateCustomerInfo } from '@photobooth/utils';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../../lib/config/terminal-urls';

export interface CustomerInfo {
  name: string;
  phone: string;
  email: string;
  instagram: string;
}

interface CustomerFormProps {
  productId: string;
  outletId: string;
  productPrice: number;
  onSubmit: (customerInfo: CustomerInfo, voucherCode?: string) => void | Promise<void>;
  onBack: () => void;
}

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

export function CustomerForm({ productId, outletId, productPrice, onSubmit, onBack }: CustomerFormProps) {
  const [formData, setFormData] = useState<CustomerInfo>({
    name: '',
    phone: '',
    email: '',
    instagram: '',
  });

  const [voucherCode, setVoucherCode] = useState('');
  const [voucherDiscount, setVoucherDiscount] = useState<number | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [isValidatingVoucher, setIsValidatingVoucher] = useState(false);

  const [errors, setErrors] = useState<Partial<Record<keyof CustomerInfo, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof CustomerInfo, boolean>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof CustomerInfo) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBlur = (field: keyof CustomerInfo) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field: keyof CustomerInfo, value: string) => {
    let error: string | undefined;

    switch (field) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;

      case 'phone':
        if (!value.trim()) {
          error = 'Phone number is required';
        } else {
          const validation = validateCustomerInfo({ phone: value });
          if (!validation.isValid) {
            const phoneError = validation.errors.find(e => e.field === 'phone');
            if (phoneError) {
              error = phoneError.message;
            }
          }
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else {
          const validation = validateCustomerInfo({ email: value });
          if (!validation.isValid) {
            const emailError = validation.errors.find(e => e.field === 'email');
            if (emailError) {
              error = emailError.message;
            }
          }
        }
        break;

      case 'instagram':
        if (value.trim() && value.trim().length < 2) {
          error = 'Instagram handle must be at least 2 characters';
        }
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleVoucherBlur = async () => {
    if (!voucherCode.trim()) {
      setVoucherDiscount(null);
      setVoucherError(null);
      return;
    }
    setIsValidatingVoucher(true);
    setVoucherError(null);
    setVoucherDiscount(null);
    try {
      const response = await apiClient.vouchers.validate({
        code: voucherCode.trim(),
        productId,
        outletId,
        customerPhone: formData.phone || undefined,
        customerEmail: formData.email || undefined,
      });
      if (response.success && response.data?.valid && response.data?.discountAmount != null) {
        setVoucherDiscount(response.data.discountAmount);
        setVoucherError(null);
      } else {
        setVoucherDiscount(null);
        setVoucherError(response.data?.message || 'Invalid voucher');
      }
    } catch {
      setVoucherDiscount(null);
      setVoucherError('Failed to validate voucher');
    } finally {
      setIsValidatingVoucher(false);
    }
  };

  const finalPrice = Math.max(0, productPrice - (voucherDiscount ?? 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Mark all fields as touched
    setTouched({
      name: true,
      phone: true,
      email: true,
      instagram: true,
    });

    // Validate all fields
    const nameValid = validateField('name', formData.name);
    const phoneValid = validateField('phone', formData.phone);
    const emailValid = validateField('email', formData.email);
    const instagramValid = validateField('instagram', formData.instagram);

    if (nameValid && phoneValid && emailValid && instagramValid) {
      setIsSubmitting(true);
      try {
        await onSubmit(
          formData,
          voucherCode.trim() && !voucherError && voucherDiscount != null ? voucherCode.trim() : undefined
        );
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to create order. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <BoothCard className="max-w-2xl mx-auto">
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">👤</div>
          <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Customer Information
          </h2>
          <p className="text-gray-600">
            Please fill in your details to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <BoothInput
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange('name')}
              onBlur={handleBlur('name')}
              className={touched.name && errors.name ? 'border-red-500' : ''}
            />
            {touched.name && errors.name && (
              <p className="mt-2 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <BoothInput
              id="phone"
              type="tel"
              placeholder="08123456789"
              value={formData.phone}
              onChange={handleChange('phone')}
              onBlur={handleBlur('phone')}
              className={touched.phone && errors.phone ? 'border-red-500' : ''}
            />
            {touched.phone && errors.phone && (
              <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <BoothInput
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              className={touched.email && errors.email ? 'border-red-500' : ''}
            />
            {touched.email && errors.email && (
              <p className="mt-2 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="voucher" className="block text-sm font-medium text-gray-700 mb-2">
              Voucher Code
            </label>
            <BoothInput
              id="voucher"
              type="text"
              placeholder="Enter voucher code (optional)"
              value={voucherCode}
              onChange={(e) => {
                setVoucherCode(e.target.value);
                setVoucherError(null);
                setVoucherDiscount(null);
              }}
              onBlur={handleVoucherBlur}
              disabled={isValidatingVoucher}
            />
            {isValidatingVoucher && <p className="mt-1 text-sm text-gray-500">Validating...</p>}
            {voucherError && <p className="mt-1 text-sm text-red-600">{voucherError}</p>}
            {voucherDiscount != null && voucherDiscount > 0 && (
              <p className="mt-1 text-sm text-green-600">
                Discount: -{new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(voucherDiscount)}. Final price: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(finalPrice)}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
              Instagram Handle
            </label>
            <BoothInput
              id="instagram"
              type="text"
              placeholder="@yourhandle"
              value={formData.instagram}
              onChange={handleChange('instagram')}
              onBlur={handleBlur('instagram')}
              className={touched.instagram && errors.instagram ? 'border-red-500' : ''}
            />
            {touched.instagram && errors.instagram && (
              <p className="mt-2 text-sm text-red-600">{errors.instagram}</p>
            )}
          </div>

          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {submitError}
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <BoothButton
              type="button"
              onClick={onBack}
              variant="outline"
              className="flex-1"
              size="lg"
              disabled={isSubmitting}
            >
              Back
            </BoothButton>
            <BoothButton
              type="submit"
              className="flex-1"
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating order...' : 'Continue to Payment'}
            </BoothButton>
          </div>
        </form>
      </div>
    </BoothCard>
  );
}
